import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminLayout from '@/layouts/AdminLayout';
import { useAuthState } from '@/hooks/useAuthState';
import { BookingService } from '@/services/bookingService';
import { getPropertyImageUrl } from '@/config/api';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { 
  Calendar, 
  Search, 
  Trash2, 
  Eye,
  ArrowLeft,
  User,
  Home,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from '@/hooks/use-toast';

interface Booking {
  id: number;
  property_id: number;
  property_title: string;
  property_cover_image?: string;
  guest_name: string;
  owner_name: string;
  check_in: string;
  check_out: string;
  status: string;
  created_at: string;
  total_price: number;
}

const AdminBookings = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const { toast } = useToast();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Estado para o input
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Verificar se é admin (AdminLayout já faz isso)

  useEffect(() => {
    loadBookings();
  }, [currentPage, itemsPerPage, searchTerm, filterStatus]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      
      // Buscar todas as reservas do banco de dados
      const allBookings = await BookingService.getAllBookings();
      
      // Mapear os dados para o formato esperado
      const mappedBookings = allBookings.map(booking => ({
        id: booking.id,
        property_id: booking.property_id,
        property_title: booking.property_title || 'Imóvel sem título',
        property_cover_image: booking.property_cover_image,
        guest_name: booking.guest_name || 'Hóspede sem nome',
        owner_name: booking.owner_name || 'Proprietário sem nome',
        check_in: booking.check_in,
        check_out: booking.check_out,
        status: booking.status,
        created_at: booking.created_at,
        total_price: booking.total_price || 0
      }));

      // Aplicar filtros
      let filteredBookings = mappedBookings;
      
      if (searchTerm) {
        filteredBookings = filteredBookings.filter(booking => 
          booking.property_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          booking.guest_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      if (filterStatus !== 'all') {
        filteredBookings = filteredBookings.filter(booking => booking.status === filterStatus);
      }

      // Calcular paginação
      const total = filteredBookings.length;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

      setBookings(paginatedBookings);
      setTotalItems(total);
    } catch (error) {
      console.error('Erro ao carregar reservas:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleDeleteBooking = async (bookingId: number) => {
    if (confirm('Tem certeza que deseja excluir esta reserva?')) {
      try {
        await BookingService.deleteBooking(bookingId);
        toast({
          title: 'Reserva excluída',
          description: 'A reserva foi removida com sucesso.',
        });
        loadBookings();
      } catch (error) {
        console.error('Erro ao excluir reserva:', error);
        toast({
          title: 'Erro ao excluir',
          description: 'Não foi possível excluir a reserva. Tente novamente.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleViewBooking = (bookingId: number) => {
    navigate(`/admin/reservas/visualizar/${bookingId}`);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'pending': return 'Pendente';
      case 'cancelled': return 'Cancelada';
      case 'rejected': return 'Rejeitada';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'rejected': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Formatar datas SEM timezone - usar exatamente como vem do banco
  // Para check_in e check_out (campos DATE): formatar diretamente da string YYYY-MM-DD
  const formatDate = (date: string | Date) => {
    if (!date) return "Data não informada";
    
    // Se for uma string no formato YYYY-MM-DD, formatar diretamente SEM criar Date object
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Se for uma string ISO com hora, extrair apenas a parte da data
    if (typeof date === 'string' && date.includes('T')) {
      const datePart = date.substring(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
      }
    }
    
    // Fallback para Date objects (não deve acontecer para check_in/check_out)
    if (date instanceof Date) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
    
    return "Data inválida";
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px] px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando reservas...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Gerenciar Reservas</h1>
            <p className="text-muted-foreground">
              Visualize e gerencie todas as reservas do sistema
            </p>
    </div>
    </div>
    </div>

      {/* Filtros e Busca */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Buscar por imóvel ou hóspede..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="pl-10"
                  />
                </div>
                <Button 
                  onClick={handleSearch}
                  variant="outline"
                  className="px-4"
                >
                  <Search className="h-4 w-4" />
                </Button>
                {searchTerm && (
                  <Button 
                    onClick={handleClearSearch}
                    variant="outline"
                    className="px-4"
                  >
                    Limpar
                  </Button>
                )}
              </div>
              {searchTerm && (
                <p className="text-sm text-muted-foreground mt-2">
                  Buscando por: <strong>"{searchTerm}"</strong>
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
              >
                Todas
              </Button>
              <Button
                variant={filterStatus === 'confirmed' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('confirmed')}
              >
                Confirmadas
              </Button>
              <Button
                variant={filterStatus === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('pending')}
              >
                Pendentes
              </Button>
              <Button
                variant={filterStatus === 'cancelled' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('cancelled')}
              >
                Canceladas
              </Button>
    </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Itens por página:</span>
              <Select value={itemsPerPage.toString()} onValueChange={(value) => handleItemsPerPageChange(parseInt(value))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
    </div>
    </div>
        </CardContent>
      </Card>

      {/* Tabela de Reservas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Reservas ({totalItems} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Imóvel</TableHead>
                <TableHead>Hóspede</TableHead>
                <TableHead>Proprietário</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Data da Reserva</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-12 h-12 rounded-lg">
                        <AvatarImage 
                          src={booking.property_cover_image && booking.property_id 
                            ? getPropertyImageUrl(booking.property_id, booking.property_cover_image)
                            : undefined
                          }
                          alt={booking.property_title}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-primary/10 rounded-lg">
                          <Home className="h-5 w-5 text-primary" />
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{booking.property_title}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      {booking.guest_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      {booking.owner_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{formatDate(booking.check_in)}</div>
                      <div className="text-muted-foreground">até</div>
                      <div>{formatDate(booking.check_out)}</div>
    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(booking.status)}>
                      {getStatusLabel(booking.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      R$ {booking.total_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(booking.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewBooking(booking.id)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Visualizar
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeleteBooking(booking.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} registros
    </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    );
                  })}
    </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </Button>
    </div>
    </div>
          </CardContent>
        </Card>
      )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBookings;
